/*
RECOVERY ARTIFACT - DO NOT TREAT AS SOURCE OF TRUTH

This file was assembled from local OpenCode session history to help recover lost unstaged work.

Sources:
- Exact saved read snapshot from session `ses_2cefd87b0ffe39P4HR7DWWGoo8`
- Part id `prt_d3102bcda001UktYAwLQi4d43x`
- That snapshot preserved lines 1-1526 of `packages/core/src/nutrition/recipe-variation/index.ts`
- No later continuation read of that same file state was found in session history
- The remainder below is copied from the current `packages/core/src/nutrition/recipe-variation/index.ts`
  starting at line 1527, because no exact saved continuation was available

Notes:
- The top portion through line 1526 is the strongest recoverable snapshot of the lost file state.
- The tail portion is only a fallback to make this easier to reconstruct manually.
- Keep `packages/core/src/nutrition/recipe-variation/index.ts` as-is until you manually recover what you want.
*/

import { z } from "zod/v4";
import { Query, type QueryRowType, type Transaction } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { withZeroTransaction } from "../../zero/utils";
import { Context, Schema, zql } from "../../zero/schema";
import { Food } from "../food";
import { FoodCategory } from "../food-category";
import { Nutrient } from "../nutrient";
import {
  accessDenied,
  createId,
  must,
  nSortIndexesBetween,
  safeParseInt,
  sort,
} from "@corpuscomplete/core/utils";
import { RecipeBase } from "../recipe-base";
import {
  recipeVariationDetailsPatchSchema,
  recipeVariationIngredientPatchDataSchema,
  RecipeVariationKind,
  recipeVariationStepPatchDataSchema,
} from "./types";
import { Actor } from "@corpuscomplete/core/actor";
import { Unit } from "@corpuscomplete/core/common/unit";

/**
 * Recipe-variation namespace boundary for patch-based derivation from recipe bases.
 *
 * Invariants:
 * - Variants are represented as typed patches over a recipe snapshot.
 * - Patch application rejects duplicate/conflicting selectors per base entity.
 * - This namespace owns custom-variant lifecycle (`createCustom`, `editCustom`, `deleteCustom`).
 * - Curated variants are managed via `Recipe.create`/`Recipe.edit` and are intentionally rejected
 *   by custom-only edit/delete flows here.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace RecipeVariation {
  export function patchQuery<
    Q extends Query<"recipeVariation", Schema, any> = Query<"recipeVariation", Schema>,
  >(q?: Q, kind?: RecipeVariationKind) {
    let query = ((q ?? zql.recipeVariation) as Query<"recipeVariation", Schema>)
      // Note: Would prefer not to have the "base" related here, but they type overrides aren't working
      // specifically when passing in or extending a related query when used inside of another related query.
      // I'm not sure if ".related("base", (q) => q.related("meta"))" + ".related("base", (q) => q.related("servings").related("meta"))"
      // Will result in over-processing or be de-duplicated
      .related("base", (q) => q.related("meta").related("categoryAssociations"))
      .related("ingredientPatches", (q) =>
        q
          .related("baseServing", (q) => q.related("food", (q) => q.related("meta")))
          .related("newServing", (q) => q.related("food", (q) => q.related("meta"))),
      )
      .related("stepPatches");
    if (kind) {
      query = query.where("kind", "=", kind);
    }
    return query;
  }
  type RawInfo = QueryRowType<ReturnType<typeof patchQuery>>;

  export const byId = defineQuery(z.object({ id: z.string() }), ({ ctx, args }) =>
    patchQuery()
      .where("id", "=", args.id)
      .whereExists("metaIfCurrent", (q) =>
        q.where((e) => Food.viewable(e, ctx.actor)).where("timeArchived", "IS", null),
      )
      .related("recipe", RecipeBase.query)
      .related("base", (q) => q.related("servings").related("meta").related("categoryAssociations"))
      .one(),
  );

  export const byIdVersion = defineQuery(
    z.object({ id: z.string(), version: z.number().positive() }),
    ({ ctx, args }) =>
      patchQuery()
        .where("id", "=", args.id)
        .where("version", "=", args.version)
        .whereExists("metaIfCurrent", (q) => q.where((e) => Food.viewable(e, ctx.actor)))
        .related("recipe", RecipeBase.query)
        .related("base", (q) =>
          q.related("servings").related("meta").related("categoryAssociations"),
        )
        .one(),
  );

  const stepPatchSchemas = {
    create: z.object({
      id: z.uuid(),
      op: z.literal("create"),
      baseSortIndex: z.null().optional(),
      newSortIndex: z.string(),
      data: recipeVariationStepPatchDataSchema.required(),
    }),
    update: z.object({
      id: z.uuid(),
      op: z.literal("update"),
      baseSortIndex: z.string(),
      newSortIndex: z.string().optional(),
      data: recipeVariationStepPatchDataSchema.nullish(),
    }),
    delete: z.object({
      id: z.uuid(),
      op: z.literal("delete"),
      baseSortIndex: z.string(),
      newSortIndex: z.null().optional(),
      data: z.null().optional(),
    }),
  };
  const stepPatchSchema = z.discriminatedUnion("op", [
    stepPatchSchemas.create,
    stepPatchSchemas.update,
    stepPatchSchemas.delete,
  ]);

  const ingredientPatchSchemas = {
    create: z.object({
      id: z.uuid(),
      op: z.literal("create"),
      baseFoodServingId: z.null().optional(),
      newFoodServingId: z.uuid(),
      data: recipeVariationIngredientPatchDataSchema.required(),
    }),
    update: z.object({
      id: z.uuid(),
      op: z.literal("update"),
      baseFoodServingId: z.uuid(),
      newFoodServingId: z.uuid().nullish(),
      data: recipeVariationIngredientPatchDataSchema.nullish(),
    }),
    delete: z.object({
      id: z.uuid(),
      op: z.literal("delete"),
      baseFoodServingId: z.uuid(),
      newFoodServingId: z.null().optional(),
      data: z.null().optional(),
    }),
  };
  const ingredientPatchSchema = z.discriminatedUnion("op", [
    ingredientPatchSchemas.create,
    ingredientPatchSchemas.update,
    ingredientPatchSchemas.delete,
  ]);

  const patchSchema = z.object({
    detailsPatch: recipeVariationDetailsPatchSchema.nullable(),
    ingredientPatches: ingredientPatchSchema.array(),
    stepPatches: stepPatchSchema.array(),
  });
  export type Patch = z.infer<typeof patchSchema>;
  export type DetailsPatch = Patch["detailsPatch"];
  export type IngredientPatch = Patch["ingredientPatches"][number];
  export type StepPatch = Patch["stepPatches"][number];

  type RecipeIngredientBase = RecipeBase.Info["ingredients"][number];
  type RecipeStepBase = RecipeBase.Info["steps"][number];

  export type PatchError =
    | {
        type: "missing-base-ingredient";
        baseFoodServingId: string;
      }
    | {
        type: "missing-base-step";
        baseSortIndex: string;
      }
    | {
        type: "duplicate-ingredient-patch-target";
        target: "base" | "new";
        foodServingId: string;
      }
    | {
        type: "duplicate-step-patch-target";
        target: "base" | "new";
        sortIndex: string;
      }
    | {
        type: "duplicate-ingredient-result-identity";
        foodServingId: string;
      }
    | {
        type: "duplicate-ingredient-result-sortIndex";
        sortIndex: string;
      }
    | {
        type: "duplicate-step-result-sortIndex";
        sortIndex: string;
      };

  export type IngredientEffect =
    | "created"
    | "deleted"
    | "conflicted"
    | "substituted"
    | "amountChanged"
    | "moved";

  export type StepEffect = "created" | "deleted" | "conflicted" | "textEdited" | "moved";

  export const ingredientEffectLabel: Record<IngredientEffect, string> = {
    conflicted: "Conflict",
    deleted: "Removed",
    created: "Added",
    substituted: "Substituted",
    amountChanged: "Amount",
    moved: "Moved",
  };

  export const stepEffectLabel: Record<StepEffect, string> = {
    conflicted: "Conflict",
    deleted: "Removed",
    created: "Added",
    textEdited: "Edited",
    moved: "Moved",
  };

  export type OpClassification<T extends string> = {
    primary: T;
    effects: T[];
  };

  /**
   * Derives the patch that would transform the base form into the next form.
   *
   * Should only be used on forms that have already been validated.
   */
  export const derivePatch = (base: RecipeBase.Form, next: RecipeBase.Form): Patch => {
    const baseIngredientSortIndexes = nSortIndexesBetween(null, null, base.ingredients.length);
    const nextIngredientSortIndexes = nSortIndexesBetween(null, null, next.ingredients.length);
    const baseIngredients = base.ingredients.map((item, index) => ({
      ...item,
      sortIndex: must(baseIngredientSortIndexes[index]),
    }));
    const nextIngredients = next.ingredients.map((item, index) => ({
      ...item,
      sortIndex: must(nextIngredientSortIndexes[index]),
    }));
    const baseStepSortIndexes = nSortIndexesBetween(null, null, base.steps.length);
    const nextStepSortIndexes = nSortIndexesBetween(null, null, next.steps.length);
    const baseSteps = base.steps.map((item, index) => ({
      ...item,
      sortIndex: must(baseStepSortIndexes[index]),
    }));
    const nextSteps = next.steps.map((item, index) => ({
      ...item,
      sortIndex: must(nextStepSortIndexes[index]),
    }));

    const detailsPatch: Patch["detailsPatch"] = {};
    if (next.recipe.description !== base.recipe.description) {
      detailsPatch.description = next.recipe.description;
    }
    if (next.recipe.prepTimeMinutes !== base.recipe.prepTimeMinutes) {
      detailsPatch.prepTimeMinutes = safeParseInt(next.recipe.prepTimeMinutes);
    }
    if (next.recipe.cookTimeMinutes !== base.recipe.cookTimeMinutes) {
      detailsPatch.cookTimeMinutes = safeParseInt(next.recipe.cookTimeMinutes);
    }

    const ingredientPatches: Patch["ingredientPatches"] = [];
    const baseIngredientsBySortIndex = new Map(
      baseIngredients.map((item) => [item.sortIndex, item]),
    );
    const nextIngredientsBySortIndex = new Map(
      nextIngredients.map((item) => [item.sortIndex, item]),
    );
    const nextIngredientsByIdentity = new Map(
      nextIngredients.map((item) => [item.foodServingId, item]),
    );
    const usedNextIngredientSortIndexes = new Set<string>();

    for (const baseIngredient of baseIngredients) {
      const nextAtBaseSortIndex = nextIngredientsBySortIndex.get(baseIngredient.sortIndex);
      if (nextAtBaseSortIndex) {
        usedNextIngredientSortIndexes.add(nextAtBaseSortIndex.sortIndex);
        if (nextAtBaseSortIndex.foodServingId === baseIngredient.foodServingId) {
          const data: NonNullable<Extract<IngredientPatch, { op: "update" }>["data"]> = {};
          if (nextAtBaseSortIndex.amount !== baseIngredient.amount) {
            data.amount = nextAtBaseSortIndex.amount;
          }
          if (nextAtBaseSortIndex.sortIndex !== baseIngredient.sortIndex) {
            data.sortIndex = nextAtBaseSortIndex.sortIndex;
          }
          if (Object.keys(data).length > 0) {
            ingredientPatches.push({
              id: createId(),
              op: "update",
              baseFoodServingId: baseIngredient.foodServingId,
              data,
            });
          }
          continue;
        }

        const data: NonNullable<Extract<IngredientPatch, { op: "update" }>["data"]> = {};
        if (nextAtBaseSortIndex.amount !== baseIngredient.amount) {
          data.amount = nextAtBaseSortIndex.amount;
        }
        if (nextAtBaseSortIndex.sortIndex !== baseIngredient.sortIndex) {
          data.sortIndex = nextAtBaseSortIndex.sortIndex;
        }

        ingredientPatches.push({
          id: createId(),
          op: "update",
          baseFoodServingId: baseIngredient.foodServingId,
          newFoodServingId: nextAtBaseSortIndex.foodServingId,
          data: Object.keys(data).length > 0 ? data : undefined,
        });
        continue;
      }

      const nextWithSameIdentity = nextIngredientsByIdentity.get(baseIngredient.foodServingId);
      if (nextWithSameIdentity) {
        usedNextIngredientSortIndexes.add(nextWithSameIdentity.sortIndex);
        const data: NonNullable<Extract<IngredientPatch, { op: "update" }>["data"]> = {};
        if (nextWithSameIdentity.amount !== baseIngredient.amount) {
          data.amount = nextWithSameIdentity.amount;
        }
        if (nextWithSameIdentity.sortIndex !== baseIngredient.sortIndex) {
          data.sortIndex = nextWithSameIdentity.sortIndex;
        }
        if (Object.keys(data).length > 0) {
          ingredientPatches.push({
            id: createId(),
            op: "update",
            baseFoodServingId: baseIngredient.foodServingId,
            data,
          });
        }
        continue;
      }

      ingredientPatches.push({
        id: createId(),
        op: "delete",
        baseFoodServingId: baseIngredient.foodServingId,
      });
    }

    for (const nextIngredient of nextIngredients) {
      if (usedNextIngredientSortIndexes.has(nextIngredient.sortIndex)) {
        continue;
      }
      const baseAtSortIndex = baseIngredientsBySortIndex.get(nextIngredient.sortIndex);
      if (baseAtSortIndex && baseAtSortIndex.foodServingId === nextIngredient.foodServingId) {
        continue;
      }
      ingredientPatches.push({
        id: createId(),
        op: "create",
        newFoodServingId: nextIngredient.foodServingId,
        data: {
          amount: nextIngredient.amount,
          sortIndex: nextIngredient.sortIndex,
        },
      });
    }

    const stepPatches: Patch["stepPatches"] = [];
    const nextStepsBySortIndex = new Map(nextSteps.map((item) => [item.sortIndex, item]));
    const baseSortIndexToMatchedNextSortIndex = new Map<string, string>();
    const usedNextStepSortIndexes = new Set<string>();
    const baseSortIndexesByText = new Map<string, string[]>();
    const nextSortIndexesByText = new Map<string, string[]>();

    for (const baseStep of baseSteps) {
      const sortIndexes = baseSortIndexesByText.get(baseStep.text) ?? [];
      sortIndexes.push(baseStep.sortIndex);
      baseSortIndexesByText.set(baseStep.text, sortIndexes);
    }
    for (const nextStep of nextSteps) {
      const sortIndexes = nextSortIndexesByText.get(nextStep.text) ?? [];
      sortIndexes.push(nextStep.sortIndex);
      nextSortIndexesByText.set(nextStep.text, sortIndexes);
    }

    for (const [text, baseSortIndexes] of baseSortIndexesByText) {
      const nextSortIndexes = nextSortIndexesByText.get(text) ?? [];
      const matchCount = Math.min(baseSortIndexes.length, nextSortIndexes.length);
      for (let index = 0; index < matchCount; index += 1) {
        baseSortIndexToMatchedNextSortIndex.set(
          must(baseSortIndexes[index]),
          must(nextSortIndexes[index]),
        );
      }
    }

    for (const baseStep of baseSteps) {
      const matchedNextSortIndex = baseSortIndexToMatchedNextSortIndex.get(baseStep.sortIndex);
      if (matchedNextSortIndex) {
        usedNextStepSortIndexes.add(matchedNextSortIndex);
        if (matchedNextSortIndex !== baseStep.sortIndex) {
          stepPatches.push({
            id: createId(),
            op: "update",
            baseSortIndex: baseStep.sortIndex,
            newSortIndex: matchedNextSortIndex,
          });
        }
        continue;
      }

      const nextStep = nextStepsBySortIndex.get(baseStep.sortIndex);
      if (nextStep && !usedNextStepSortIndexes.has(nextStep.sortIndex)) {
        usedNextStepSortIndexes.add(nextStep.sortIndex);
        stepPatches.push({
          id: createId(),
          op: "update",
          baseSortIndex: baseStep.sortIndex,
          data: { text: nextStep.text },
        });
        continue;
      }

      stepPatches.push({ id: createId(), op: "delete", baseSortIndex: baseStep.sortIndex });
    }

    for (const nextStep of nextSteps) {
      if (usedNextStepSortIndexes.has(nextStep.sortIndex)) {
        continue;
      }
      stepPatches.push({
        id: createId(),
        op: "create",
        newSortIndex: nextStep.sortIndex,
        data: { text: nextStep.text },
      });
    }

    return {
      detailsPatch: Object.keys(detailsPatch).length > 0 ? detailsPatch : null,
      ingredientPatches,
      stepPatches,
    };
  };

  export function asInfo(raw: RawInfo | undefined) {
    if (!raw?.base?.meta) {
      return undefined;
    }
    return {
      ...raw,
      base: raw.base,
      meta: {
        ...raw.base.meta,
        isArchived: raw.base.meta.timeArchived !== null,
      },
      categoryAssociations: raw.base.categoryAssociations,
      ingredientPatches: raw.ingredientPatches.flatMap((rawPatch) => {
        const patchResult = ingredientPatchSchema.safeParse(rawPatch);
        if (!patchResult.success) {
          return [];
        }
        const hydrated = hydrateIngredientPatch(patchResult.data, rawPatch.newServing);
        if (hydrated) {
          return hydrated;
        }
        return [];
      }),
      stepPatches: raw.stepPatches.flatMap((rawPatch) => {
        const patchResult = stepPatchSchema.safeParse(rawPatch);
        if (!patchResult.success) {
          return [];
        }
        return patchResult.data;
      }),
    };
  }
  export type Info = NonNullable<ReturnType<typeof asInfo>>;

  export type HydratedPatch = Pick<Info, "detailsPatch" | "ingredientPatches" | "stepPatches">;

  // TODO: Should this check viewability while here?
  async function hydratePatch(tx: Transaction, patch: Patch): Promise<HydratedPatch> {
    const ingredientServingIds = new Set<string>();
    for (const { newFoodServingId } of patch.ingredientPatches) {
      if (newFoodServingId) {
        ingredientServingIds.add(newFoodServingId);
      }
    }

    const results = await tx.run(
      zql.foodServing
        .where("id", "IN", Array.from(ingredientServingIds))
        .related("food", (q) => q.related("meta")),
    );
    const servingById = new Map(results.map((s) => [s.id, s]));

    const ingredientPatches: Info["ingredientPatches"] = [];
    for (const ingredientPatch of patch.ingredientPatches) {
      const maybeServing = ingredientPatch.newFoodServingId
        ? servingById.get(ingredientPatch.newFoodServingId)
        : undefined;
      const hydrated = hydrateIngredientPatch(ingredientPatch, maybeServing);
      if (hydrated) {
        ingredientPatches.push(hydrated);
      }
    }

    return {
      ...patch,
      ingredientPatches,
    };
  }

  function hydrateIngredientPatch(
    ingredientPatch: Patch["ingredientPatches"][number],
    maybeNewServing: Food.MaybeItem["serving"],
  ) {
    if (!maybeNewServing?.food?.meta) {
      return null;
    }
    const {
      food: { meta: newFoodMeta, ...newFood },
      ...newServing
    } = maybeNewServing;
    return { ...ingredientPatch, newFood: { ...newFood, meta: newFoodMeta }, newServing };
  }

  const applyDetailsPatch = (
    base: RecipeBase.Info,
    { detailsPatch }: HydratedPatch,
  ): Pick<RecipeBase.Info, "description" | "prepTimeMinutes" | "cookTimeMinutes"> => {
    return {
      description: detailsPatch?.description ?? base.description,
      prepTimeMinutes: detailsPatch?.prepTimeMinutes ?? base.prepTimeMinutes,
      cookTimeMinutes: detailsPatch?.cookTimeMinutes ?? base.cookTimeMinutes,
    };
  };

  const applyIngredientPatches = (
    base: RecipeBase.Info,
    { ingredientPatches }: HydratedPatch,
  ): { value: RecipeIngredientBase[]; errors: PatchError[] } => {
    if (ingredientPatches.length === 0) {
      return {
        value: base.ingredients.map((ingredient) => ({ ...ingredient })),
        errors: [],
      };
    }

    const errors: PatchError[] = [];
    const baseSorted = sort(base.ingredients);
    const baseByKey = new Map(baseSorted.map((item) => [item.foodServingId, item]));
    const seenBase = new Set<string>();
    const deletions = new Set<string>();
    const updates = new Map<
      string,
      Extract<HydratedPatch["ingredientPatches"][number], { op: "update" }>
    >();
    const createPatches: Array<
      Extract<HydratedPatch["ingredientPatches"][number], { op: "create" }>
    > = [];
    const seenCreateTargets = new Set<string>();

    for (const patch of ingredientPatches) {
      if (patch.op === "create") {
        if (
          !patch.newFoodServingId ||
          patch.data?.amount === undefined ||
          patch.data.sortIndex === undefined
        ) {
          continue;
        }

        if (seenCreateTargets.has(patch.newFoodServingId)) {
          errors.push({
            type: "duplicate-ingredient-patch-target",
            target: "new",
            foodServingId: patch.newFoodServingId,
          });
          continue;
        }

        seenCreateTargets.add(patch.newFoodServingId);
        createPatches.push(patch);
        continue;
      }

      if (!patch.baseFoodServingId) {
        continue;
      }

      if (!baseByKey.has(patch.baseFoodServingId)) {
        errors.push({
          type: "missing-base-ingredient",
          baseFoodServingId: patch.baseFoodServingId,
        });
        continue;
      }

      if (seenBase.has(patch.baseFoodServingId)) {
        errors.push({
          type: "duplicate-ingredient-patch-target",
          target: "base",
          foodServingId: patch.baseFoodServingId,
        });
        continue;
      }
      seenBase.add(patch.baseFoodServingId);

      if (patch.op === "delete") {
        deletions.add(patch.baseFoodServingId);
        continue;
      }

      updates.set(patch.baseFoodServingId, patch);
    }

    const candidates: RecipeIngredientBase[] = [];
    for (const baseIngredient of baseSorted) {
      if (deletions.has(baseIngredient.foodServingId)) {
        continue;
      }

      const update = updates.get(baseIngredient.foodServingId);
      candidates.push({
        // TODO: Pass in ids from base
        id: "",
        recipeId: base.id,
        recipeVersion: base.version,
        food: update?.newFood ?? baseIngredient.food,
        serving: update?.newServing ?? baseIngredient.serving,
        foodServingId: update?.newFoodServingId ?? baseIngredient.foodServingId,
        amount: update?.data?.amount ?? baseIngredient.amount,
        sortIndex: update?.data?.sortIndex ?? baseIngredient.sortIndex,
      });
    }

    for (const created of createPatches) {
      candidates.push({
        // TODO: Pass in ids from base
        id: "",
        recipeId: base.id,
        recipeVersion: base.version,
        food: created.newFood,
        serving: created.newServing,
        foodServingId: created.newFoodServingId,
        amount: created.data.amount,
        sortIndex: created.data.sortIndex,
      });
    }

    const value: RecipeIngredientBase[] = [];
    const seenIdentities = new Set<string>();
    const seenSortIndexes = new Set<string>();

    for (const ingredient of candidates) {
      if (seenIdentities.has(ingredient.foodServingId)) {
        errors.push({
          type: "duplicate-ingredient-result-identity",
          foodServingId: ingredient.foodServingId,
        });
        continue;
      }

      if (seenSortIndexes.has(ingredient.sortIndex)) {
        errors.push({
          type: "duplicate-ingredient-result-sortIndex",
          sortIndex: ingredient.sortIndex,
        });
        continue;
      }

      seenIdentities.add(ingredient.foodServingId);
      seenSortIndexes.add(ingredient.sortIndex);
      value.push(ingredient);
    }

    return {
      value: [...sort(value)],
      errors,
    };
  };

  const applyStepPatches = (
    base: RecipeBase.Info,
    { stepPatches }: HydratedPatch,
  ): { value: RecipeStepBase[]; errors: PatchError[] } => {
    if (stepPatches.length === 0) {
      return {
        value: base.steps.map((step) => ({ ...step })),
        errors: [],
      };
    }

    const errors: PatchError[] = [];
    const baseSorted = sort(base.steps);
    const baseByKey = new Map(baseSorted.map((item) => [item.sortIndex, item]));
    const seenBase = new Set<string>();
    const seenCreateTargets = new Set<string>();
    const deletions = new Set<string>();
    const updates = new Map<string, { newSortIndex?: string | null | undefined; text?: string }>();
    const createPatches: Array<{ sortIndex: string; text: string }> = [];

    for (const patch of stepPatches) {
      if (patch.op === "create") {
        if (!patch.newSortIndex || patch.data?.text === undefined) {
          continue;
        }

        if (seenCreateTargets.has(patch.newSortIndex)) {
          errors.push({
            type: "duplicate-step-patch-target",
            target: "new",
            sortIndex: patch.newSortIndex,
          });
          continue;
        }

        seenCreateTargets.add(patch.newSortIndex);
        createPatches.push({ sortIndex: patch.newSortIndex, text: patch.data.text });
        continue;
      }

      if (!patch.baseSortIndex) {
        continue;
      }

      const baseKey = patch.baseSortIndex;
      if (!baseByKey.has(baseKey)) {
        errors.push({ type: "missing-base-step", baseSortIndex: baseKey });
        continue;
      }

      if (seenBase.has(baseKey)) {
        errors.push({
          type: "duplicate-step-patch-target",
          target: "base",
          sortIndex: baseKey,
        });
        continue;
      }
      seenBase.add(baseKey);

      if (patch.op === "delete") {
        deletions.add(baseKey);
        continue;
      }

      updates.set(baseKey, {
        newSortIndex: patch.newSortIndex,
        text: patch.data?.text,
      });
    }

    const candidates: RecipeStepBase[] = [];
    for (const baseStep of baseSorted) {
      if (deletions.has(baseStep.sortIndex)) {
        continue;
      }

      const update = updates.get(baseStep.sortIndex);
      candidates.push({
        id: baseStep.id,
        recipeId: base.id,
        recipeVersion: base.version,
        text: update?.text ?? baseStep.text,
        sortIndex: update?.newSortIndex ?? baseStep.sortIndex,
      });
    }

    for (const created of createPatches) {
      candidates.push({
        // TODO: What to use here?
        id: createId(),
        recipeId: base.id,
        recipeVersion: base.version,
        text: created.text,
        sortIndex: created.sortIndex,
      });
    }

    const value: RecipeStepBase[] = [];
    const seenSortIndexes = new Set<string>();
    for (const step of candidates) {
      if (seenSortIndexes.has(step.sortIndex)) {
        errors.push({
          type: "duplicate-step-result-sortIndex",
          sortIndex: step.sortIndex,
        });
        continue;
      }

      seenSortIndexes.add(step.sortIndex);
      value.push(step);
    }

    return {
      value: [...sort(value)],
      errors,
    };
  };

  /**
   * Applies variant patches to a recipe base snapshot.
   *
   * Contract:
   * - Missing base selectors are reported as typed errors (not throws).
   * - Duplicate patch targets/result identities are reported as typed errors.
   * - Patch mechanics do not enforce recipe-content invariants (for example,
   *   empty ingredient sets); those are validated at save time.
   */
  // TODO: This should probably return a more narrow type than RecipeBase.Info
  // It's annoying to have the nested ids and the timestamps on this function which is just supposed to be applying a patch to a recipe base
  export const applyPatch = (
    base: RecipeBase.Info,
    patch: HydratedPatch,
    variation: {
      id: string;
      version: number;
      base: Food.BaseInput;
      meta: Food.MetaInput & { isArchived: boolean };
      categoryAssociations: readonly Omit<Food.CategoryAssociation, "foodId" | "foodVersion">[];
    },
  ): { value: RecipeBase.Info; errors: PatchError[] } => {
    const ingredients = applyIngredientPatches(base, patch);
    const steps = applyStepPatches(base, patch);
    const { description, prepTimeMinutes, cookTimeMinutes } = applyDetailsPatch(base, patch);
    return {
      value: {
        id: variation.id,
        version: variation.version,
        serving: base.serving,
        description,
        prepTimeMinutes,
        cookTimeMinutes,
        base: variation.base,
        meta: variation.meta,
        categoryAssociations: variation.categoryAssociations,
        ingredients: ingredients.value,
        steps: steps.value,
      },
      errors: [...ingredients.errors, ...steps.errors],
    };
  };

  const applyPatchErrorMessage = (error: PatchError): string => {
    switch (error.type) {
      case "missing-base-ingredient":
        return `Ingredient patch target not found: ${error.baseFoodServingId}`;
      case "missing-base-step":
        return `Step patch target not found: ${error.baseSortIndex}`;
      case "duplicate-ingredient-patch-target":
        return `Duplicate ingredient patch for ${error.target} ${error.foodServingId}`;
      case "duplicate-step-patch-target":
        return `Duplicate step patch for ${error.target} ${error.sortIndex}`;
      case "duplicate-ingredient-result-identity":
        return `Duplicate ingredient in result: ${error.foodServingId}`;
      case "duplicate-ingredient-result-sortIndex":
        return `Duplicate ingredient sortIndex in result: "${error.sortIndex}"`;
      case "duplicate-step-result-sortIndex":
        return `Duplicate step in result: ${error.sortIndex}`;
    }
  };

  // const ingredientEffectPriority: readonly IngredientEffect[] = [
  //   "conflicted",
  //   "deleted",
  //   "created",
  //   "substituted",
  //   "amountChanged",
  //   "moved",
  // ];

  // const stepEffectPriority: readonly StepEffect[] = [
  //   "conflicted",
  //   "deleted",
  //   "created",
  //   "textEdited",
  //   "moved",
  // ];

  // const classifyEffects = <T extends string>(
  //   effects: readonly T[],
  //   priority: readonly T[],
  // ): OpClassification<T> | null => {
  //   const effectSet = new Set<T>(effects);
  //   if (effectSet.size === 0) {
  //     return null;
  //   }

  //   const orderedEffects = priority.filter((item) => effectSet.has(item));
  //   const primary = orderedEffects[0];
  //   if (!primary) {
  //     throw new Error("Patch classification produced no primary effect");
  //   }

  //   return {
  //     primary,
  //     effects: orderedEffects,
  //   };
  // };

  // const mergeClassification = <T extends string>(
  //   map: Record<string, OpClassification<T>>,
  //   key: string,
  //   effects: readonly T[],
  //   priority: readonly T[],
  // ) => {
  //   const existing = map[key];
  //   const merged = existing ? [...existing.effects, ...effects] : [...effects];
  //   const classification = classifyEffects(merged, priority);
  //   if (!classification) {
  //     return;
  //   }
  //   map[key] = classification;
  // };

  // const mergeOperationClassification = <T extends string>(
  //   priority: readonly T[],
  //   ...classifications: Array<OpClassification<T> | undefined>
  // ): OpClassification<T> | null => {
  //   const effects = classifications.flatMap((item) => item?.effects ?? []);
  //   return classifyEffects(effects, priority);
  // };

  // const classifyPatch = (
  //   base: RecipeBase,
  //   patch: Patch,
  // ): {
  //   ingredientByBaseKey: Record<string, OpClassification<IngredientEffect>>;
  //   ingredientByNewSortIndex: Record<string, OpClassification<IngredientEffect>>;
  //   removedIngredientCount: number;
  //   stepByBaseSortIndex: Record<string, OpClassification<StepEffect>>;
  //   stepByNewSortIndex: Record<string, OpClassification<StepEffect>>;
  //   removedStepCount: number;
  //   errors: PatchError[];
  // } => {
  //   const applied = applyPatch(base, {
  //     detailsPatch: null,
  //     ingredientPatches: patch.ingredientPatches,
  //     stepPatches: patch.stepPatches,
  //   });

  //   const ingredientByBaseKey: Record<string, OpClassification<IngredientEffect>> = {};
  //   const ingredientByNewSortIndex: Record<string, OpClassification<IngredientEffect>> = {};
  //   const stepByBaseSortIndex: Record<string, OpClassification<StepEffect>> = {};
  //   const stepByNewSortIndex: Record<string, OpClassification<StepEffect>> = {};

  //   let removedIngredientCount = 0;
  //   let removedStepCount = 0;

  //   const baseIngredientsByKey = new Map(
  //     base.ingredients.map((item) => [item.foodServingId, item]),
  //   );
  //   const baseStepsBySortIndex = new Map(base.steps.map((item) => [item.sortIndex, item]));

  //   for (const ingredientPatch of patch.ingredientPatches) {
  //     if (ingredientPatch.op === "create") {
  //       if (ingredientPatch.data?.sortIndex === undefined) {
  //         continue;
  //       }
  //       mergeClassification(
  //         ingredientByNewSortIndex,
  //         ingredientPatch.data.sortIndex,
  //         ["created"],
  //         ingredientEffectPriority,
  //       );
  //       continue;
  //     }

  //     if (!ingredientPatch.baseFoodServingId) {
  //       continue;
  //     }

  //     if (ingredientPatch.op === "delete") {
  //       mergeClassification(ingredientByBaseKey, ingredientPatch.baseFoodServingId, ["deleted"], ingredientEffectPriority);
  //       removedIngredientCount += 1;
  //       continue;
  //     }

  //     const effects: IngredientEffect[] = [];
  //     if (ingredientPatch.newFoodServingId) {
  //       effects.push("substituted");
  //     }
  //     if (ingredientPatch.data?.amount !== undefined) {
  //       effects.push("amountChanged");
  //     }
  //     if (ingredientPatch.data?.sortIndex !== undefined) {
  //       effects.push("moved");
  //     }

  //     mergeClassification(ingredientByBaseKey, ingredientPatch.baseFoodServingId, effects, ingredientEffectPriority);

  //     const baseIngredient = baseIngredientsByKey.get(ingredientPatch.baseFoodServingId);
  //     const newSortIndex = ingredientPatch.data?.sortIndex ?? baseIngredient?.sortIndex;
  //     if (newSortIndex) {
  //       mergeClassification(
  //         ingredientByNewSortIndex,
  //         newSortIndex,
  //         effects,
  //         ingredientEffectPriority,
  //       );
  //     }
  //   }

  //   for (const stepPatch of patch.stepPatches) {
  //     if (stepPatch.op === "create") {
  //       if (!stepPatch.newSortIndex) {
  //         continue;
  //       }
  //       mergeClassification(
  //         stepByNewSortIndex,
  //         stepPatch.newSortIndex,
  //         ["created"],
  //         stepEffectPriority,
  //       );
  //       continue;
  //     }

  //     const baseSortIndex = stepPatch.baseSortIndex;
  //     if (!baseSortIndex) {
  //       continue;
  //     }
  //     if (stepPatch.op === "delete") {
  //       mergeClassification(stepByBaseSortIndex, baseSortIndex, ["deleted"], stepEffectPriority);
  //       removedStepCount += 1;
  //       continue;
  //     }

  //     const effects: StepEffect[] = [];
  //     if (stepPatch.data?.text !== undefined) {
  //       effects.push("textEdited");
  //     }
  //     if (stepPatch.newSortIndex) {
  //       effects.push("moved");
  //     }

  //     mergeClassification(stepByBaseSortIndex, baseSortIndex, effects, stepEffectPriority);

  //     const newSortIndex =
  //       stepPatch.newSortIndex ?? baseStepsBySortIndex.get(baseSortIndex)?.sortIndex;
  //     if (newSortIndex) {
  //       mergeClassification(stepByNewSortIndex, newSortIndex, effects, stepEffectPriority);
  //     }
  //   }

  //   for (const error of applied.errors) {
  //     if (error.type === "missing-base-ingredient") {
  //       mergeClassification(ingredientByBaseKey, error.baseFoodServingId, ["conflicted"], ingredientEffectPriority);
  //       continue;
  //     }

  //     if (error.type !== "missing-base-step") {
  //       continue;
  //     }

  //     mergeClassification(
  //       stepByBaseSortIndex,
  //       error.baseSortIndex,
  //       ["conflicted"],
  //       stepEffectPriority,
  //     );
  //   }

  //   return {
  //     ingredientByBaseKey,
  //     ingredientByNewSortIndex,
  //     removedIngredientCount,
  //     stepByBaseSortIndex,
  //     stepByNewSortIndex,
  //     removedStepCount,
  //     errors: applied.errors,
  //   };
  // };

  // export type MaterializedIngredient = Food.Item & {
  //   id: string;
  //   sortIndex: string;
  // };

  // export type MaterializedStep = RecipeStepBase & {
  //   id: string;
  // };

  // export type Materialized = {
  //   ingredients: MaterializedIngredient[];
  //   steps: MaterializedStep[];
  //   ingredientMetaByKey: Record<
  //     string,
  //     {
  //       marker: OpClassification<IngredientEffect> | null;
  //     }
  //   >;
  //   stepMetaByKey: Record<
  //     string,
  //     {
  //       marker: OpClassification<StepEffect> | null;
  //     }
  //   >;
  //   hasConflicts: boolean;
  //   removedIngredients: Food.Item[];
  //   removedSteps: MaterializedStep[];
  // };

  // type MaterializeVariant = Pick<
  //   Info,
  //   "detailsPatch" | "ingredientPatches" | "stepPatches" | "id" | "version" | "kind"
  // >;

  // const createdMarker = <T extends string>(effect: T): OpClassification<T> => ({
  //   primary: effect,
  //   effects: [effect],
  // });

  // type RecipeIngredientRow = Recipe.Info["ingredients"][number];

  // const asFoodInfo = (food: Food.Info | undefined | null): Food.Info | null => food ?? null;

  // const ingredientRowFood = (ingredient: RecipeIngredientRow): Food.Info | null =>
  //   asFoodInfo(ingredient.serving?.food) ?? asFoodInfo(ingredient.food);

  // const buildServingLookup = (
  //   baseRecipe: Recipe.Info,
  //   ingredientPatches: readonly MaterializeVariant["ingredientPatches"][number][],
  // ): Map<string, { food: Food.Info; serving: Food.Serving }> => {
  //   const servingLookup = new Map<string, Recipe.Info['ingredients'][number]>();
  //   for (const ingredient of baseRecipe.ingredients) {
  //     servingLookup.set(
  //       ingredient.foodServingId,
  //       ingredient,
  //     );
  //   }

  //   for (const ingredientPatch of ingredientPatches) {
  //     const baseServingFood = asFoodInfo(ingredientPatch.baseServing?.food);
  //     if (ingredientPatch.baseServing?.food && baseServingFood) {
  //       servingLookup.set(
  //         ingredientKey({
  //           foodId: ingredientPatch.baseServing.food.id,
  //           foodServingId: ingredientPatch.baseServing.id,
  //         }),
  //         {
  //           food: baseServingFood,
  //           serving: ingredientPatch.baseServing,
  //         },
  //       );
  //     }
  //     const newServingFood = asFoodInfo(ingredientPatch.newServing?.food);
  //     if (ingredientPatch.newServing?.food && newServingFood) {
  //       servingLookup.set(
  //         ingredientKey({
  //           foodId: ingredientPatch.newServing.food.id,
  //           foodServingId: ingredientPatch.newServing.id,
  //         }),
  //         {
  //           food: newServingFood,
  //           serving: ingredientPatch.newServing,
  //         },
  //       );
  //     }
  //   }

  //   return servingLookup;
  // };

  // const buildIngredientAnchors = (
  //   baseIngredientByAnchor: Map<string, RecipeIngredient>,
  //   ingredientPatches: readonly MaterializeVariant["ingredientPatches"][number][],
  // ) => {
  //   const ingredientCreatePatchIdByKey = new Map<string, string>();
  //   const ingredientAnchorByKey = new Map<string, string>();

  //   for (const ingredientPatch of ingredientPatches) {
  //     if (ingredientPatch.op === "create") {
  //       const patchData = ingredientPatch.data;
  //       if (
  //         !patchData ||
  //         !patchData.sortIndex ||
  //         !ingredientPatch.newFoodId ||
  //         !ingredientPatch.newFoodServingId
  //       ) {
  //         continue;
  //       }
  //       ingredientCreatePatchIdByKey.set(
  //         ingredientKey({
  //           foodId: ingredientPatch.newFoodId,
  //           foodServingId: ingredientPatch.newFoodServingId,
  //         }),
  //         ingredientPatch.id,
  //       );
  //       continue;
  //     }

  //     if (ingredientPatch.op !== "update") {
  //       continue;
  //     }

  //     if (!ingredientPatch.baseFoodId || !ingredientPatch.baseFoodServingId) {
  //       continue;
  //     }

  //     const baseAnchorKey = ingredientKey({
  //       foodId: ingredientPatch.baseFoodId,
  //       foodServingId: ingredientPatch.baseFoodServingId,
  //     });
  //     const baseIngredient = baseIngredientByAnchor.get(baseAnchorKey);
  //     const nextFoodId =
  //       ingredientPatch.newFoodId ??
  //       (baseIngredient ? ingredientRowFood(baseIngredient)?.id : undefined) ??
  //       baseIngredient?.serving?.foodId;
  //     const nextFoodServingId = ingredientPatch.newFoodServingId ?? baseIngredient?.foodServingId;
  //     if (!nextFoodId || !nextFoodServingId) {
  //       continue;
  //     }

  //     ingredientAnchorByKey.set(
  //       ingredientKey({
  //         foodId: nextFoodId,
  //         foodServingId: nextFoodServingId,
  //       }),
  //       baseAnchorKey,
  //     );
  //   }

  //   return {
  //     ingredientCreatePatchIdByKey,
  //     ingredientAnchorByKey,
  //   };
  // };

  // const buildStepAnchors = (stepPatches: readonly MaterializeVariant["stepPatches"][number][]) => {
  //   const stepCreatePatchIdBySortIndex = new Map<string, string>();
  //   const stepBaseAnchorBySortIndex = new Map<string, string>();

  //   for (const stepPatch of stepPatches) {
  //     if (stepPatch.op === "create") {
  //       if (!stepPatch.newSortIndex) {
  //         continue;
  //       }
  //       stepCreatePatchIdBySortIndex.set(stepPatch.newSortIndex, stepPatch.id);
  //       continue;
  //     }
  //     if (stepPatch.op !== "update") {
  //       continue;
  //     }
  //     if (!stepPatch.baseSortIndex) {
  //       continue;
  //     }
  //     stepBaseAnchorBySortIndex.set(
  //       stepPatch.newSortIndex ?? stepPatch.baseSortIndex,
  //       stepPatch.baseSortIndex,
  //     );
  //   }

  //   return {
  //     stepCreatePatchIdBySortIndex,
  //     stepBaseAnchorBySortIndex,
  //   };
  // };

  // const materializeIngredientRows = ({
  //   resolvedIngredients,
  //   baseIngredientByAnchor,
  //   servingLookup,
  //   ingredientCreatePatchIdByKey,
  //   ingredientAnchorByKey,
  //   operationClassification,
  // }: {
  //   resolvedIngredients: readonly RecipeIngredientBase[];
  //   baseIngredientByAnchor: Map<string, RecipeIngredientRow>;
  //   servingLookup: Map<string, { food: Food.Info; serving: Food.Serving }>;
  //   ingredientCreatePatchIdByKey: Map<string, string>;
  //   ingredientAnchorByKey: Map<string, string>;
  //   operationClassification: ReturnType<typeof classifyPatch>;
  // }): {
  //   ingredients: MaterializedIngredient[];
  //   ingredientMetaByKey: Materialized["ingredientMetaByKey"];
  // } => {
  //   const ingredients: MaterializedIngredient[] = [];
  //   const ingredientMetaByKey: Materialized["ingredientMetaByKey"] = {};

  //   for (const ingredient of sort(resolvedIngredients)) {
  //     const key = ingredientKey(ingredient);
  //     const createdPatchId = ingredientCreatePatchIdByKey.get(key);
  //     const baseAnchorKey = createdPatchId
  //       ? null
  //       : (ingredientAnchorByKey.get(key) ?? (baseIngredientByAnchor.has(key) ? key : null));

  //     const servingSource = servingLookup.get(key);
  //     if (!servingSource) {
  //       continue;
  //     }

  //     if (createdPatchId) {
  //       const item: MaterializedIngredient = {
  //         id: createdPatchId,
  //         key: `new:${ingredient.foodId}:${ingredient.foodServingId}:${ingredient.sortIndex}`,
  //         amount: ingredient.amount,
  //         sortIndex: ingredient.sortIndex,
  //         food: servingSource.food,
  //         serving: servingSource.serving,
  //       };
  //       ingredients.push(item);
  //       ingredientMetaByKey[item.key] = {
  //         marker:
  //           operationClassification.ingredientByNewSortIndex[ingredient.sortIndex] ??
  //           createdMarker("created"),
  //       };
  //       continue;
  //     }

  //     if (!baseAnchorKey) {
  //       continue;
  //     }

  //     const baseIngredient = baseIngredientByAnchor.get(baseAnchorKey);
  //     if (!baseIngredient) {
  //       continue;
  //     }

  //     const item: MaterializedIngredient = {
  //       id: baseIngredient.id,
  //       key: `base:${baseAnchorKey}`,
  //       amount: ingredient.amount,
  //       sortIndex: ingredient.sortIndex,
  //       food: servingSource.food,
  //       serving: servingSource.serving,
  //     };
  //     ingredients.push(item);
  //     ingredientMetaByKey[item.key] = {
  //       marker: mergeOperationClassification(
  //         ingredientEffectPriority,
  //         operationClassification.ingredientByBaseKey[baseAnchorKey],
  //         operationClassification.ingredientByNewSortIndex[ingredient.sortIndex],
  //       ),
  //     };
  //   }

  //   return {
  //     ingredients,
  //     ingredientMetaByKey,
  //   };
  // };

  // const materializeStepRows = ({
  //   resolvedSteps,
  //   baseStepBySortIndex,
  //   stepCreatePatchIdBySortIndex,
  //   stepBaseAnchorBySortIndex,
  //   operationClassification,
  // }: {
  //   resolvedSteps: readonly RecipeStepBase[];
  //   baseStepBySortIndex: Map<string, Recipe.Info["steps"][number]>;
  //   stepCreatePatchIdBySortIndex: Map<string, string>;
  //   stepBaseAnchorBySortIndex: Map<string, string>;
  //   operationClassification: ReturnType<typeof classifyPatch>;
  // }): {
  //   steps: MaterializedStep[];
  //   stepMetaByKey: Materialized["stepMetaByKey"];
  // } => {
  //   const steps: MaterializedStep[] = [];
  //   const stepMetaByKey: Materialized["stepMetaByKey"] = {};

  //   for (const step of sort(resolvedSteps)) {
  //     const createdPatchId = stepCreatePatchIdBySortIndex.get(step.sortIndex);
  //     const baseAnchorSortIndex = createdPatchId
  //       ? null
  //       : (stepBaseAnchorBySortIndex.get(step.sortIndex) ??
  //         (baseStepBySortIndex.has(step.sortIndex) ? step.sortIndex : null));

  //     if (createdPatchId) {
  //       const item: MaterializedStep = {
  //         id: createdPatchId,
  //         key: `new:${step.sortIndex}`,
  //         text: step.text,
  //         sortIndex: step.sortIndex,
  //       };
  //       steps.push(item);
  //       stepMetaByKey[item.key] = {
  //         marker:
  //           operationClassification.stepByNewSortIndex[step.sortIndex] ?? createdMarker("created"),
  //       };
  //       continue;
  //     }

  //     if (!baseAnchorSortIndex) {
  //       continue;
  //     }

  //     const baseStep = baseStepBySortIndex.get(baseAnchorSortIndex);
  //     if (!baseStep) {
  //       continue;
  //     }

  //     const item: MaterializedStep = {
  //       id: baseStep.id,
  //       key: `base:${baseAnchorSortIndex}`,
  //       text: step.text,
  //       sortIndex: step.sortIndex,
  //     };
  //     steps.push(item);
  //     stepMetaByKey[item.key] = {
  //       marker: mergeOperationClassification(
  //         stepEffectPriority,
  //         operationClassification.stepByBaseSortIndex[baseAnchorSortIndex],
  //         operationClassification.stepByNewSortIndex[step.sortIndex],
  //       ),
  //     };
  //   }

  //   return {
  //     steps,
  //     stepMetaByKey,
  //   };
  // };

  // const collectRemovedIngredients = (
  //   baseIngredientByAnchor: Map<string, RecipeIngredientRow>,
  //   ingredientPatches: readonly MaterializeVariant["ingredientPatches"][number][],
  // ): Food.Item[] => {
  //   const removedIngredients: Food.Item[] = [];
  //   const removedIngredientAnchors = new Set<string>();

  //   for (const ingredientPatch of ingredientPatches) {
  //     if (ingredientPatch.op !== "delete") {
  //       continue;
  //     }
  //     const baseAnchorKey = ingredientKey({
  //       foodId: must(ingredientPatch.baseFoodId),
  //       foodServingId: must(ingredientPatch.baseFoodServingId),
  //     });
  //     if (removedIngredientAnchors.has(baseAnchorKey)) {
  //       continue;
  //     }
  //     removedIngredientAnchors.add(baseAnchorKey);

  //     const baseIngredient = baseIngredientByAnchor.get(baseAnchorKey);
  //     if (!baseIngredient) {
  //       continue;
  //     }
  //     const item = Food.asItem({
  //       amount: baseIngredient.amount,
  //       serving: baseIngredient.serving,
  //       food: ingredientRowFood(baseIngredient),
  //     });
  //     if (item) {
  //       removedIngredients.push(item);
  //     }
  //   }

  //   return removedIngredients;
  // };

  // const collectRemovedSteps = (
  //   baseStepBySortIndex: Map<string, Recipe.Info["steps"][number]>,
  //   stepPatches: readonly MaterializeVariant["stepPatches"][number][],
  // ): MaterializedStep[] => {
  //   const removedStepSortIndexes = new Set<string>();
  //   const removedSteps: MaterializedStep[] = [];

  //   for (const stepPatch of stepPatches) {
  //     if (stepPatch.op !== "delete") {
  //       continue;
  //     }
  //     if (!stepPatch.baseSortIndex) {
  //       continue;
  //     }
  //     if (removedStepSortIndexes.has(stepPatch.baseSortIndex)) {
  //       continue;
  //     }
  //     removedStepSortIndexes.add(stepPatch.baseSortIndex);

  //     const baseStep = baseStepBySortIndex.get(stepPatch.baseSortIndex);
  //     if (!baseStep) {
  //       continue;
  //     }
  //     removedSteps.push({
  //       id: baseStep.id,
  //       key: `base:${stepPatch.baseSortIndex}`,
  //       text: baseStep.text,
  //       sortIndex: baseStep.sortIndex,
  //     });
  //   }

  //   return removedSteps;
  // };

  /**
   * Materializes a display-ready variant preview from a base recipe + patch rows.
   *
   * Contract:
   * - Returns marker metadata per rendered row for UI badges.
   * - `hasConflicts` is true when patch selectors reference missing base rows.
   * - Includes explicit removed ingredient/step summaries for preview UIs.
   */
  // export const materialize = (
  //   baseRecipe: Recipe.Info,
  //   variant: MaterializeVariant,
  // ): Materialized => {
  //   const patch = toVariantPatch(variant);
  //   const applied = applyPatch(baseRecipe, patch);
  //   const operationClassification = classifyPatch(baseRecipe, {
  //     ingredientPatches: patch.ingredientPatches,
  //     stepPatches: patch.stepPatches,
  //   });
  //   const resolved = applied.value;

  //   const baseIngredientByAnchor = new Map(
  //     baseRecipe.ingredients.map((ingredient) => [ingredientKey(ingredient), ingredient]),
  //   );
  //   const baseStepBySortIndex = new Map(baseRecipe.steps.map((step) => [step.sortIndex, step]));
  //   const servingLookup = buildServingLookup(baseRecipe, variant.ingredientPatches);
  //   const { ingredientCreatePatchIdByKey, ingredientAnchorByKey } = buildIngredientAnchors(
  //     baseIngredientByAnchor,
  //     variant.ingredientPatches,
  //   );
  //   const { stepCreatePatchIdBySortIndex, stepBaseAnchorBySortIndex } = buildStepAnchors(
  //     variant.stepPatches,
  //   );
  //   throw new Error("Not implemented");
  // }

  // export const materialize = (
  //   baseRecipe: Recipe.Info,
  //   variant: MaterializeVariant,
  // ): Materialized => {
  //   const patch = toVariantPatch(variant);
  //   const recipeBase: RecipeBase = {
  //     description: baseRecipe.description,
  //     prepTimeMinutes: baseRecipe.prepTimeMinutes,
  //     cookTimeMinutes: baseRecipe.cookTimeMinutes,
  //     ingredients: baseRecipe.ingredients.map((ingredient) => ({
  //       foodId: must(ingredientRowFood(ingredient)?.id ?? ingredient.serving?.foodId),
  //       foodServingId: ingredient.foodServingId,
  //       amount: ingredient.amount,
  //       sortIndex: ingredient.sortIndex,
  //     })),
  //     steps: baseRecipe.steps.map((step) => ({ text: step.text, sortIndex: step.sortIndex })),
  //   };

  //   const applied = applyPatch(recipeBase, patch);
  //   const operationClassification = classifyPatch(recipeBase, {
  //     ingredientPatches: patch.ingredientPatches,
  //     stepPatches: patch.stepPatches,
  //   });
  //   const resolved = applied.value;

  //   const baseIngredientByAnchor = new Map(
  //     baseRecipe.ingredients.map((ingredient) => [
  //       ingredientKey(ingredientSelectorFromRow(ingredient)),
  //       ingredient,
  //     ]),
  //   );
  //   const baseStepBySortIndex = new Map(baseRecipe.steps.map((step) => [step.sortIndex, step]));
  //   const servingLookup = buildServingLookup(baseRecipe, variant.ingredientPatches);
  //   const { ingredientCreatePatchIdByKey, ingredientAnchorByKey } = buildIngredientAnchors(
  //     baseIngredientByAnchor,
  //     variant.ingredientPatches,
  //   );
  //   const { stepCreatePatchIdBySortIndex, stepBaseAnchorBySortIndex } = buildStepAnchors(
  //     variant.stepPatches,
  //   );

  //   const { ingredients, ingredientMetaByKey } = materializeIngredientRows({
  //     resolvedIngredients: resolved.ingredients,
  //     baseIngredientByAnchor,
  //     servingLookup,
  //     ingredientCreatePatchIdByKey,
  //     ingredientAnchorByKey,

  // TODO: Assert that recipe is viewable?
  const loadRecipeBase = async (
    tx: Transaction,
    ctx: Context,
    recipeId: string,
    recipeVersion: number,
  ): Promise<RecipeBase.Info> => {
    const canViewBase = await Food.canView(tx, ctx.actor, recipeId);
    if (!canViewBase) {
      accessDenied("You do not have permission to view this recipe");
    }
    const recipe = await tx.run(
      RecipeBase.query().where("id", "=", recipeId).where("version", "=", recipeVersion).one(),
    );
    const meta = recipe?.base?.meta;
    const info = RecipeBase.asInfo(recipe);
    if (!info || !meta) {
      throw new Error(`Recipe not found. id: "${recipeId}" version: "${recipeVersion}"`);
    }
    if (meta.currentVersion !== recipeVersion) {
      throw new Error("Base recipe has been modified. Please refresh and try again.");
    }
    if (meta.timeArchived) {
      throw new Error("Base recipe has been archived. Please refresh and try again.");
    }
    return info;
  };

  export const create = async (
    tx: Transaction,
    actor: Actor,
    {
      id,
      base,
      categoryAssociations,
      recipeId,
      recipeVersion,
      meta,
      servingId,
      ...patch
    }: CreateCustom,
    recipeBase: RecipeBase.Info,
    {
      metaOp,
      kind,
      version,
      timeCreated,
      timeUpdated,
    }: {
      metaOp: "insert" | "update";
      kind: RecipeVariationKind;
      version: number;
      timeCreated: Temporal.Instant;
      timeUpdated: Temporal.Instant;
    },
  ) => {
    const [hydrated, units] = await Promise.all([
      hydratePatch(tx, patch),
      Unit.byType(tx, ["mass", "volume"]),
    ]);
    const patchResult = applyPatch(recipeBase, hydrated, {
      id,
      version,
      base,
      meta: { ...meta, isArchived: false },
      categoryAssociations,
    });
    if (patchResult.errors.length > 0) {
      throw new Error(
        `Recipe variant patch is invalid: ${patchResult.errors.map(applyPatchErrorMessage).join("; ")}`,
      );
    }
    const resolved = patchResult.value;

    // TODO: Consider trying to reuse a recipe module schema here except it was a bit annoying because those schemas expect an ID on ingredient and step
    // The only non-structural variant for now is the ingredients must have a length greater than one, so we may need a test for this.
    // If any other non-structural validation happens in the schemas, we might want to re-visit this.
    if (resolved.ingredients.length === 0) {
      throw new Error("Recipe variant must have at least one ingredient");
    }

    const canViewIngredients = await Food.canViewByServingIdBatch(
      tx,
      actor,
      resolved.ingredients.map((ingredient) => ingredient.foodServingId),
    );
    if (!canViewIngredients) {
      accessDenied("You do not have permission to use one or more ingredients");
    }

    const nutrientAmounts = Nutrient.addFoodItems(Nutrient.emptyAmounts(), resolved.ingredients);
    const nutrientRows = await Nutrient.amountsToRowsForKeys(tx, nutrientAmounts);

    if (metaOp === "update") {
      await tx.mutate.foodMeta.update({
        id,
        ...meta,
        timeUpdated: timeUpdated,
      });
    } else {
      await tx.mutate.foodMeta.insert({
        id,
        type: "recipe_variation",
        ...meta,
        currentVersion: null,
        timeCreated: timeCreated,
        timeUpdated: timeUpdated,
      });
    }

    await tx.mutate.food.insert({
      id,
      version,
      name: base.name,
      modifiers: base.modifiers,
      iconSlugs: base.iconSlugs ?? null,
      timeCreated,
    });

    await tx.mutate.foodMeta.update({
      id,
      currentVersion: version,
    });

    await tx.mutate.recipeVariation.insert({
      id,
      version,
      recipeId,
      recipeVersion,
      kind,
      detailsPatch: patch.detailsPatch,
    });

    await FoodCategory.addAssociations(tx, {
      foodId: id,
      foodVersion: version,
      isACategoryIds: categoryAssociations.map((association) => association.categoryId),
      ingredientFoods: resolved.ingredients.map((ingredient) => ({
        foodId: ingredient.serving.foodId,
        foodVersion: ingredient.serving.foodVersion,
      })),
    });

    const promises: Promise<any>[] = [];
    promises.push(
      Promise.all(
        nutrientRows.map(({ nutrientId, amount }) =>
          tx.mutate.recipeVariationNutrient.insert({
            recipeVariationId: id,
            recipeVariationVersion: version,
            nutrientId,
            amount,
          }),
        ),
      ),
    );

    const serving: Food.ServingInput = {
      id: servingId,
      name: recipeBase.serving.name,
      defaultAmount: 1,
      modifiers: [],
      mass:
        recipeBase.serving.massQuantity && recipeBase.serving.massUnitKey
          ? {
              quantity: recipeBase.serving.massQuantity,
              unitKey: recipeBase.serving.massUnitKey,
            }
          : null,
      volume:
        recipeBase.serving.volumeQuantity && recipeBase.serving.volumeUnitKey
          ? {
              quantity: recipeBase.serving.volumeQuantity,
              unitKey: recipeBase.serving.volumeUnitKey,
            }
          : null,
      nutrients: Nutrient.scaleAmounts(nutrientAmounts, 1 / recipeBase.serving.perRecipe),
    };

    Food.assertNoDuplicateUnitServings([serving], units);

    const servings = Food.sortServings(Food.addUnitConversionServings([serving], units), units);
    promises.push(
      Promise.all(
        servings.map((serving) =>
          tx.mutate.foodServing.insert({
            ...Food.servingInputToRow(serving),
            foodId: id,
            foodVersion: version,
          }),
        ),
      ),
    );

    promises.push(
      Promise.all(
        patch.ingredientPatches.map((item) =>
          tx.mutate.recipeVariationIngredientPatch.insert({
            ...item,
            recipeVariationId: id,
            recipeVariationVersion: version,
          }),
        ),
      ),
    );
    promises.push(
      Promise.all(
        patch.stepPatches.map((item) =>
          tx.mutate.recipeVariationStepPatch.insert({
            ...item,
            recipeVariationId: id,
            recipeVariationVersion: version,
          }),
        ),
      ),
    );

    await Promise.all(promises);
  };

  export const curatedSchema = patchSchema.extend({
    id: z.uuid(),
    servingId: z.uuid(),
    base: Food.baseSchema(),
    categoryAssociations: Food.categoryAssociationsSchema(),
  });

  export type CreateCustom = z.infer<typeof createCustom.schema>;
  export const createCustom = withZeroTransaction(
    patchSchema
      .extend({
        id: z.uuid(),
        base: Food.baseSchema(),
        meta: Food.metaSchema(),
        categoryAssociations: Food.categoryAssociationsSchema(),
        servingId: z.uuid(),
        recipeId: z.uuid(),
        recipeVersion: z.number(),
      })
      .strict(),
    async ({ tx, ctx, args }) => {
      const canAdd = await Food.canAddToCollection(
        tx,
        ctx.actor,
        args.meta.collectionId,
        "recipe_variation",
      );
      if (!canAdd) {
        accessDenied("You do not have permission to add a recipe variant to this collection");
      }

      const recipeBase = await loadRecipeBase(tx, ctx, args.recipeId, args.recipeVersion);
      const timeNow = Temporal.Now.instant();

      await create(tx, ctx.actor, args, recipeBase, {
        metaOp: "insert",
        kind: "custom",
        version: 1,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });
    },
  );

  /**
   * Edit an existing custom recipe variant.
   *
   * Curated variants are intentionally out of scope for this mutator. Use `Recipe.edit` for
   * curated variation updates/archival.
   */
  export const editCustom = withZeroTransaction(
    createCustom.schema.extend({
      currentVersion: z.number(),
    }),
    async ({ tx, ctx, args }) => {
      const canEditResult = await Food.canEdit(
        tx,
        ctx.actor,
        {
          id: args.id,
          currentVersion: args.currentVersion,
          type: "recipe_variation",
        },
        { op: "edit", collectionId: args.meta.collectionId },
      );
      if (!canEditResult.ok) {
        accessDenied(canEditResult.error);
      }
      const { currentMeta, newVersion } = canEditResult;

      const recipeBase = await loadRecipeBase(tx, ctx, args.recipeId, args.recipeVersion);
      const timeNow = Temporal.Now.instant();
      await create(tx, ctx.actor, args, recipeBase, {
        metaOp: "update",
        kind: "custom",
        version: newVersion,
        timeCreated: currentMeta.timeCreated,
        timeUpdated: timeNow,
      });
    },
  );

  export const archiveCustom = withZeroTransaction(
    z.object({ id: z.uuid(), currentVersion: z.number() }),
    async ({ tx, ctx, args: { id, currentVersion } }) => {
      await Food.archive(tx, ctx.actor, { id, currentVersion, type: "recipe_variation" });
    },
  );
}
